import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckSquare, Search, Settings2, Square } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface Library {
  id: string
  name: string
  description: string
}

interface LibraryPickerProps {
  availableLibraries: Library[]
  selectedLibraries: string[]
  onToggleLibrary: (libraryId: string) => void
  /** True when nothing is explicitly stored, so `selectedLibraries` is the
   * implicit "all libraries" fallback rather than a choice the user made.
   *
   * Without this the two states are indistinguishable: unchecking the last
   * library persists `[]`, which the caller immediately renders as everything
   * checked again — so the final click silently undoes itself and the row
   * claims an explicit selection that the backend will ignore in favour of the
   * flow-wide default (graph_builder.py treats an empty list as unset). */
  isUsingDefault?: boolean
}

export function LibraryPicker({
  availableLibraries,
  selectedLibraries,
  onToggleLibrary,
  isUsingDefault = false,
}: LibraryPickerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.agentNode" })
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const filteredLibraries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return availableLibraries
    return availableLibraries.filter(
      (lib) =>
        lib.name.toLowerCase().includes(query) || lib.description.toLowerCase().includes(query)
    )
  }, [availableLibraries, searchQuery])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className="nodrag -m-0.5 size-6 p-0"
          size="icon"
          variant="ghost"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center">
                <Settings2 className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {t("libraryPicker.configureLibraries")}
                <span className="ml-1 opacity-70">
                  {isUsingDefault
                    ? `(${t("libraryPicker.flowDefaultShort")})`
                    : `(${selectedLibraries.length}/${availableLibraries.length})`}
                </span>
              </p>
            </TooltipContent>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="nodrag nowheel w-[320px] p-0"
        side="right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2 left-2 h-4 w-4" />
            <Input
              className="h-8 pl-8 text-sm"
              placeholder={t("libraryPicker.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        {isUsingDefault && (
          // Say out loud that nothing is explicitly selected. The rows below are
          // all checked, but that is the fallback, not a stored choice — without
          // this the picker looks like it silently reverted the user's last click.
          <div className="text-muted-foreground px-3 pb-2 text-xs">
            {t("libraryPicker.usingFlowDefault")}
          </div>
        )}
        <div className="max-h-[280px] overflow-y-auto p-2 pt-0">
          {filteredLibraries.length === 0 ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              {t("libraryPicker.noResults")}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredLibraries.map((library) => {
                const isActive = selectedLibraries.includes(library.id)
                return (
                  <button
                    key={library.id}
                    type="button"
                    className={cn(
                      "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                      isActive ? "bg-primary/10 ring-primary/30 ring-1" : "hover:bg-muted/50"
                    )}
                    onClick={() => onToggleLibrary(library.id)}
                  >
                    <span
                      className={cn(
                        "mt-0.5 shrink-0",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                    >
                      {isActive ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{library.name}</div>
                      {library.description && (
                        <div className="text-muted-foreground line-clamp-1 text-xs">
                          {library.description}
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
